import React, { useState } from "react";
import { Box, VStack, Flex, Button, Heading, Text, Image } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom"; 
import TickIcon from "./TickIcon.svg";
import Arrow from "./CallToActionArrowButton.svg";
import { BASE_API_URL } from '../config';

const PaymentPlan = ({ activePlanGuid }) => {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const navigate = useNavigate();
  const premiumPlanGuid = "298eb053-7c3a-45d6-967f-f73508517aa9";
  const isPremiumCurrent = activePlanGuid === premiumPlanGuid;
  const isBasicCurrent = !activePlanGuid;

  const premiumPricingByPeriod = {
    monthly: {
      price: "$18.90",
      originalPrice: "$22.90",
      periodLabel: "per user per month",
      stripePriceId: "price_1QuB0bC7LkUNUnN8wWYhK9vI",
      renewalType: "MONTHLY"
    },
    six_month: {
      price: "$94.90",
      originalPrice: "$114.90",
      periodLabel: "per user per 6 months",
      stripePriceId: "price_1SVY4oC7LkUNUnN83hvwRsqt",
      renewalType: "SIX_MONTH"
    },
    yearly: {
      price: "$159.90",
      originalPrice: "$179.90",
      periodLabel: "per user per year",
      stripePriceId: "price_1QuBAlC7LkUNUnN8MzPrfUzq",
      renewalType: "YEARLY"
    },
  };
  
  const currentPremiumPricing = premiumPricingByPeriod[billingPeriod];

  const plans = [
    {
      PlanType: "Basic Plan",
      Price: "$0",
      period: "per user per month",
      features: [ 
        "Access to unlimited past papers across multiple exam boards and qualifications",
        "Instant marking and feedback on limited number of questions daily",
      ],
    },
    {
      PlanType: "Premium Plan",
      Price: currentPremiumPricing.price,
      originalPrice: currentPremiumPricing.originalPrice,
      period: currentPremiumPricing.periodLabel,
      isPopular: true,
      features: [
        "Instant, Detailed Feedback: Get deep insights into your answers.",
        "Performance Tracking: Our dashboard breaks down your performance by subject and individual topics.",
        "Personalized Study Suggestions: Based on your performance, we suggest key topics to focus on to maximize your scores.",
        "Overall Subject Performance Insights: See how you're performing across an entire subject by analyzing individual topic strengths and weaknesses.",
        "Test Mode: Experience real exam conditions with Test Mode: timed sessions, full-screen lock, no copy-pasting, controlled navigation, and optional session recording for a secure and fair testing environment. "
      ],
    },
  ];

  const commonButtonProps = {
    variant: "unstyled",
    borderRadius: "14px",
    bg: "#4BFF5C",
    width: "180px",
    border: "1px solid",
    borderColor: "#4BFF5C",
    color: "#021A04",
    _hover: {
      color: "#282A29",
      borderColor: "#E7EAE8",
      backgroundColor: "transparent",
    },
  };
  
  const filteredPlans = plans.filter(plan => !(plan.PlanType === "Basic Plan" && isPremiumCurrent));

  const isAuthenticated = () => {
    return localStorage.getItem('accessToken') !== null;
  };

  const handleGetStarted = (planType) => {
    if (!isAuthenticated()) {
      sessionStorage.setItem('selectedPlan', planType);
      navigate('/login');
    } else {
      sessionStorage.setItem('selectedPlan', planType);  
      handleCheckout();
    }
  };

  const handleCheckout = async () => {
    const token = `Bearer ${localStorage.getItem('accessToken')}`;
    try {
      const response = await fetch(`${BASE_API_URL}/api/v1/user-subscription-plans/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          subscription_plan_guid: premiumPlanGuid,
          stripe_price_id: currentPremiumPricing.stripePriceId,
          renewal_type: currentPremiumPricing.renewalType,
        }),
      });
      if (!response.ok) throw new Error('Failed to create checkout session');
      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Error creating checkout session:', err.message);
    }
  };

  const handleManageSubscriptionClick = async () => {
    const token = `Bearer ${localStorage.getItem("accessToken")}`;
    try {
      const userResponse = await fetch(`${BASE_API_URL}/api/v1/users/own`, {
        headers: { Authorization: token },
      });
      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userData = await userResponse.json();
      const portalRes = await fetch(
        `${BASE_API_URL}/api/v1/user-subscription-plans/stripe/create-billing-portal-session/${userData.stripe_customer_id}`,
        { method: "POST", headers: { "Content-Type": "application/json", Authorization: token } }
      );
      if (!portalRes.ok) throw new Error("Failed to create billing portal");
      const portalData = await portalRes.json();
      window.location.href = portalData.billing_portal_url;
    } catch (err) {
      console.error("Error creating billing portal session:", err.message);
    }
  };

  return (
    <Box width="100%" paddingX={{ base: "20px", md: "45px" }} marginTop="60px">
      <VStack spacing={8} alignItems="center">
        <Flex flexDirection="column" alignItems="center" width={{ base: "100%", lg: "60%" }} gap="14px" marginBottom="40px">
          <Heading color="#021A04" fontSize={{ base: "24px", md: "32px" }} fontWeight="500" textAlign="center">
            We have got a plan that is perfect for you
          </Heading>
          <Flex
            borderRadius="14px"
            border="1px solid #D7D7D7"
            alignItems="center"
            gap="4px"
            padding="3px"
          >
            <Button
              variant="unstyled"
              width="121px"
              padding="10px 34px"
              borderRadius="14px"
              bg={billingPeriod === "monthly" ? "#4BFF5C" : "transparent"}
              color="#08250B"
              border={billingPeriod === "monthly" ? "none" : "1px solid #E7EAE8"}
              fontSize="12px"
              fontWeight="400"
              onClick={() => setBillingPeriod("monthly")}
            >
              Monthly
            </Button>

            <Button
              variant="unstyled"
              width="121px"
              padding="10px 34px"
              borderRadius="14px"
              bg={billingPeriod === "six_month" ? "#4BFF5C" : "transparent"}
              color="#08250B"
              border={billingPeriod === "six_month" ? "none" : "1px solid #E7EAE8"}
              fontSize="12px"
              fontWeight="400"
              onClick={() => setBillingPeriod("six_month")}
            >
              6-Month
            </Button>

            <Button
              variant="unstyled"
              width="121px"
              padding="10px 34px"
              borderRadius="14px"
              bg={billingPeriod === "yearly" ? "#4BFF5C" : "transparent"}
              color="#08250B"
              border={billingPeriod === "yearly" ? "none" : "1px solid #E7EAE8"}
              fontSize="12px"
              fontWeight="400"
              onClick={() => setBillingPeriod("yearly")}
            >
              Annual
            </Button>
          </Flex>
        </Flex>
        <Flex flexDirection={{ base: "column", lg: "row" }} gap={{ base: "40px", lg: "50px" }} width="100%" justifyContent="center">
          {filteredPlans.map((plan, index) => {
            const isCurrentPlan = (plan.PlanType === "Basic Plan" && isBasicCurrent) || (plan.PlanType === "Premium Plan" && isPremiumCurrent);
            return (
              <Flex key={index} width={{ base: "100%", md: "450px" }} padding="30px 20px" flexDirection="column" bg="#ffffff" border="2px solid #E7EAE8" borderRadius="14px" gap="30px" alignItems="center" textAlign="center">
                <Flex justifyContent="space-between" width="100%" alignItems="center">
                  <Heading color="#282A29" fontSize={{ base: "20px", md: "24px" }} fontWeight="500">
                    {plan.PlanType}
                  </Heading>
                  {plan.isPopular && (
                    <Flex display="inline-flex" padding="4px 10px" justifyContent="center" alignItems="center" gap="10px" borderRadius="24px" bg="linear-gradient(240deg, #0FFD26 12.06%, #7BFC87 85.61%)" color="#021A04" fontSize="12px" fontWeight="500" lineHeight="normal">
                      Popular
                    </Flex>
                  )}
                </Flex>
                <Flex flexDirection="row" alignItems="center" gap="8px">
                  {plan.originalPrice && <Text color="#6B6C6C" fontSize={{ base: "18px", md: "20px" }} fontWeight="400" textDecoration="line-through" textDecorationThickness="1px">{plan.originalPrice}</Text>}
                  <Heading color="#282A29" fontSize={{ base: "30px", md: "36px" }} fontWeight="500">{plan.Price}</Heading>
                  <Flex flexDirection="column" alignItems="flex-start">
                    <Text color="#282A29" fontSize="11px" fontWeight="400">per user</Text>
                    <Text color="#343634" fontSize="11px" fontWeight="400">{plan.period.split(" ").slice(2).join(" ")}</Text>
                  </Flex>
                </Flex>
                {isCurrentPlan ? (
                <Button
                  {...commonButtonProps}
                  bg="transparent"
                  borderColor="gray.300"
                  color="#021A04"
                  _hover={{}}
                  isDisabled
                  cursor="not-allowed"
                  onClick={plan.PlanType === "Premium Plan" ? handleManageSubscriptionClick : undefined}
                >
                  <Flex align="center" justify="center" px="8px" py="4px">
                    <Heading fontSize="12px" fontWeight="400">
                      {plan.PlanType === "Premium Plan"
                        ? "Manage Subscription"
                        : "This is your current plan"}
                    </Heading>
                  </Flex>
                </Button>
              ) : (
                <Button
                  {...commonButtonProps}
                  onClick={() => handleGetStarted(plan.PlanType)}
                >
                  <Flex align="center" justify="center" px="8px" py="4px" gap="4px">
                    <Heading fontSize="12px" fontWeight="400">
                      Get Started
                    </Heading>
                    <Image src={Arrow} alt="get-started-arrow-button-icon" />
                  </Flex>
                </Button>
              )} 

                <Flex flexDirection="column" alignItems="flex-start" gap="18px">
                  <Heading fontSize="18px" color="#282A29" fontWeight="500">FEATURES</Heading>
                  {plan.features.map((feature, i) => (
                    <Flex key={i} flexDirection="row" gap="8px" alignItems="center" textAlign="left">
                      <Image src={TickIcon} alt="tick-plan-icon" />
                      {feature.includes(":") ? (
                        <Text color="#282A29" fontSize="14px" fontWeight="500">{feature.split(":")[0]}:<Text as="span" color="#343634" fontSize="14px" fontWeight="400"> {feature.split(":")[1]}</Text></Text>
                      ) : (
                        <Text color="#343634" fontSize="14px" fontWeight="400">{feature}</Text>
                      )}
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </VStack>
    </Box>
  );
};

export default PaymentPlan;
